import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { careersWhyData, jobsData, hiringProcessData } from "@/mocks/careersData";

export default function CareersPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Careers" },
        ]}
      />

      <PageHero
        badge="Careers At AstroJal"
        headline="Build Your Career With AstroJal"
        subtext="Join our team and work on modern Laravel, web, mobile, cloud and AI projects."
        primaryCTA="View Open Positions"
        secondaryCTA="Submit Your Resume"
      />

      {/* Why Work With Us */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-14">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                Why AstroJal
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                Why Work With Us
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {careersWhyData.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-14">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                Open Roles
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                Open Positions
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                We're always looking for talented people. Find a role that fits and apply today.
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {jobsData.map((job, i) => (
              <AnimatedSection key={job.slug} delay={i * 100}>
                <div className="flex flex-col gap-5 p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-13 h-13 sm:w-14 sm:h-14 shrink-0 flex items-center justify-center rounded-xl text-white"
                      style={{ backgroundColor: job.color }}
                    >
                      <i className={`${job.icon} text-xl`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold text-foreground-950 font-heading">{job.title}</h3>
                        <span className="px-2.5 py-0.5 rounded-full bg-accent-50 text-accent-600 text-xs font-semibold whitespace-nowrap">
                          {job.type}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                        <span className="flex items-center gap-1.5 whitespace-nowrap">
                          <i className="ri-briefcase-line" />
                          {job.experience}
                        </span>
                        <span className="flex items-center gap-1.5 whitespace-nowrap">
                          <i className="ri-map-pin-line" />
                          {job.location}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {job.description}
                  </p>

                  <div className="pt-2 border-t border-background-200/60">
                    <a
                      href={`/careers/${job.slug}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/20 whitespace-nowrap btn-arrow-hover group"
                    >
                      View Job &amp; Apply
                      <i className="ri-arrow-right-line btn-arrow text-xs transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* General Resume Application */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <AnimatedSection direction="right">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                  General Application
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                  Don&apos;t See the Right Role?
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  Send us your resume and tell us how you could contribute to AstroJal. We're always
                  open to speaking with talented engineers, designers and product people — even when
                  there isn't an open listing yet.
                </p>
                <ul className="flex flex-col gap-3 mb-8">
                  {[
                    "We review every application personally.",
                    "Great matches are added to our talent pool.",
                    "We reach out when the right role opens up.",
                  ].map((point) => (
                    <li key={point} className="flex items-start gap-3 text-sm text-foreground-700">
                      <span className="w-5 h-5 mt-0.5 shrink-0 flex items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
                        <i className="ri-check-line text-xs" />
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
                <a
                  href="/careers/apply"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
                >
                  Submit Your Resume
                  <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <p className="mt-4 text-sm text-foreground-500">
                  Prefer email? Send your resume to{" "}
                  <a
                    href="mailto:hr@astrojal.com"
                    className="text-accent-600 font-semibold hover:underline"
                  >
                    hr@astrojal.com
                  </a>
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection direction="left" delay={100}>
              <div className="rounded-2xl overflow-hidden bg-background-100 border border-background-200/60">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20team%20collaboration%20workspace%20illustration%2C%20diverse%20software%20engineers%20working%20together%20on%20laptops%2C%20abstract%20blue%20purple%20and%20orange%20gradient%20tech%20aesthetic%2C%20floating%20UI%20panels%20and%20code%20elements%2C%20premium%20minimal%20digital%20art%20style%2C%20no%20text&width=800&height=560&seq=astrojal-careers-team&orientation=landscape"
                  alt="AstroJal team collaboration"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Hiring Process */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-14">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                How It Works
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                Our Hiring Process
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {hiringProcessData.map((step, i) => (
              <AnimatedSection key={step.step} delay={i * 100}>
                <div className="relative flex flex-col p-6 md:p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F97316] font-heading">
                      {step.step}
                    </span>
                    {i < hiringProcessData.length - 1 && (
                      <i className="ri-arrow-right-line text-accent-400 hidden lg:block" />
                    )}
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading="Ready to Build Something Great?"
        text="Join a team that builds modern digital products with Laravel, cloud, mobile and AI. Find your role or send us your resume."
        primaryCTA="View Open Positions"
        secondaryCTA="Send Your Resume"
      />
    </PageLayout>
  );
}